import { z } from 'zod';
import { ADMIN_PASSWORD_MINIMUM } from './lists.ts';

/**
 * AN ADMINISTRATOR'S OWN ACCOUNT.
 *
 * Everything on this file is about the signed-in person acting on themselves,
 * which is why it is not in `auth.ts` (getting IN) and not in `access.ts`
 * (one administrator acting on another). The distinction is load-bearing: the
 * audit reasons list argues that a person altering their OWN record owes no
 * justification, so nothing here carries a reason code, and `access.ts`'s
 * controls all do.
 *
 * ── THE RULE THAT DECIDES WHAT EACH ROUTE ASKS FOR ────────────────────────
 *
 * **You prove the credential you are changing.**
 *
 *   · change the password  → present the current PASSWORD
 *   · re-mint recovery codes, or replace the authenticator
 *                          → present a CODE from the current factor
 *
 * Not "always ask for both", which reads as thorough and is actually weaker
 * thinking: it would mean somebody who has lost their authenticator cannot
 * change a password they still know, and somebody who has lost their password
 * cannot replace an authenticator they still hold. Each control then needs an
 * escape hatch, and escape hatches are where bypasses live.
 *
 * And not "the session was two-factored at sign-in, so that is enough" — a
 * session is eight hours long and may be sitting unlocked on a desk. The proof
 * is presented at the moment of the change.
 *
 * ── WHAT IS DELIBERATELY NOT HERE ─────────────────────────────────────────
 *
 * "I have lost my authenticator." That is not self-service and must not become
 * it: a console account with no confirmed factor is one where a password alone
 * reaches the enrolment step, and the enrolment step mints a verified session.
 * So it goes to a Super Admin holding `access.manage`, and the screen draws the
 * control disabled saying exactly that rather than hiding it — somebody in that
 * situation needs to be told where to go, not shown nothing.
 */

/* ── What the account screen opens with ──────────────────────────────────── */

/**
 * Named `adminOwnAccount` rather than `adminAccountStatus`, which `lists.ts`
 * already uses for the lifecycle enum (INVITED / ACTIVE / SUSPENDED). "Own" is
 * also the distinction this whole file turns on, so the name earns its keep.
 *
 * `recoveryCodesRemaining` is a COUNT, and there is no route anywhere that
 * returns the codes already minted. A person must be able to see how many they
 * have left without seeing them — the player realm's status endpoint makes the
 * same split for the same reason, and it is why the count and the re-mint are
 * two endpoints rather than one endpoint with a flag.
 */
export const adminOwnAccount = z.object({
  email: z.string(),
  displayName: z.string(),
  role: z.string(),
  /** Null until the first change — a seeded or invited account has never set one. */
  passwordUpdatedAt: z.iso.datetime().nullable(),
  passwordMinimum: z.literal(ADMIN_PASSWORD_MINIMUM),

  /** A CONFIRMED factor. An enrolment that was issued and never proved is not one. */
  twoFactorConfirmedAt: z.iso.datetime().nullable(),
  /** Unspent. Zero is a real answer and the screen says so rather than hiding the row. */
  recoveryCodesRemaining: z.number().int().nonnegative(),
  /**
   * A replacement authenticator issued and not yet confirmed, with its deadline.
   * The screen uses this to offer "finish setting up the new one" instead of
   * starting again, which is what somebody who closed the tab needs.
   */
  pendingTwoFactorExpiresAt: z.iso.datetime().nullable(),

  /** Other live sessions, not counting this one. */
  otherSessions: z.number().int().nonnegative(),
  /** Browsers that may skip the second factor. */
  trustedDevices: z.number().int().nonnegative(),
});
export type AdminOwnAccount = z.infer<typeof adminOwnAccount>;

/* ── Changing the password ───────────────────────────────────────────────── */

export const changeAdminPasswordRequest = z.object({
  currentPassword: z.string().min(1, 'Type your current password.'),
  newPassword: z
    .string()
    .min(
      ADMIN_PASSWORD_MINIMUM,
      `Choose at least ${ADMIN_PASSWORD_MINIMUM} characters — this account can move money.`,
    )
    .max(200),
});
export type ChangeAdminPasswordRequest = z.infer<typeof changeAdminPasswordRequest>;

/**
 * The counts are what the screen reports back, and they are the two consequences
 * a person does not expect: their other sessions ended, and the browsers they
 * told to remember them will ask for a code again.
 *
 * `stillSignedIn` is a literal rather than a boolean because it is a PROMISE
 * about this route and not a result that varies: changing your own password from
 * inside a verified session must not throw you out of the tab you did it in. The
 * cookie is re-minted against the new token version. A reset link, which is a
 * different situation — nobody is signed in — does the opposite and says so.
 */
export const changeAdminPasswordResult = z.object({
  otherSessionsEnded: z.number().int().nonnegative(),
  trustedDevicesEnded: z.number().int().nonnegative(),
  stillSignedIn: z.literal(true),
});
export type ChangeAdminPasswordResult = z.infer<typeof changeAdminPasswordResult>;

/* ── The second factor ──────────────────────────────────────────────────── */

/**
 * One shape for every control that spends a factor, so a caller cannot send a
 * TOTP where a recovery code is accepted or the other way round — both are
 * accepted everywhere, and which one it is comes off its shape.
 *
 * 6 to 40 characters: six digits at the bottom, and a dashed recovery code at
 * the top with room to spare.
 */
export const adminFactorProof = z.object({
  code: z
    .string()
    .trim()
    .min(6, 'Type the six-digit code from your authenticator, or a recovery code.')
    .max(40),
});
export type AdminFactorProof = z.infer<typeof adminFactorProof>;

/**
 * Re-minting recovery codes RETIRES every unspent code first.
 *
 * So the answer carries the whole new set, shown exactly once, and `retired` is
 * how many were thrown away — which the screen must report, because somebody
 * with a printout in a drawer needs to know it is now waste paper.
 */
export const mintAdminRecoveryCodesResult = z.object({
  codes: z.array(z.string()).min(1),
  retired: z.number().int().nonnegative(),
});
export type MintAdminRecoveryCodesResult = z.infer<typeof mintAdminRecoveryCodesResult>;

/**
 * Starting a REPLACEMENT authenticator. The live one keeps working throughout.
 *
 * `secret` and `uri` carry the seed, so the route sends `no-store` and nothing
 * logs this body. `qr` is the same URI drawn, for somebody enrolling at a desktop
 * with the authenticator on their phone — the one case a tappable link cannot
 * serve, because the link is on the wrong device. It is NULLABLE: the encoder
 * tops out at a size a long issuer and a long address can exceed, and that is a
 * reason the picture cannot be drawn rather than a reason the enrolment cannot
 * proceed, so the screen says so in words where the symbol would have been.
 */
export const startAdminTwoFactorResult = z.object({
  secret: z.string(),
  uri: z.string(),
  qr: z.string().nullable(),
  expiresAt: z.iso.datetime(),
});
export type StartAdminTwoFactorResult = z.infer<typeof startAdminTwoFactorResult>;

/**
 * Confirming the replacement promotes it and ends every session but this one —
 * the factor that guarded them has changed, so a session minted under the old
 * one should not outlive it.
 *
 * Fresh recovery codes come with it, unavoidably: the old ones were minted
 * against the old secret's enrolment and a person who has just replaced their
 * authenticator has no way to know which of their codes still mean anything. So
 * they are all retired and a new set is returned, shown once.
 */
export const confirmAdminTwoFactorResult = z.object({
  confirmedAt: z.iso.datetime(),
  codes: z.array(z.string()).min(1),
  otherSessionsEnded: z.number().int().nonnegative(),
  trustedDevicesEnded: z.number().int().nonnegative(),
  stillSignedIn: z.literal(true),
});
export type ConfirmAdminTwoFactorResult = z.infer<typeof confirmAdminTwoFactorResult>;
